import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { LayoutComposer } from '../PageBuilder/PageBuilder';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import ReviewContent from './components/ReviewContent';
import ResalePlatformSection from './components/ResalePlatformSection';
import { Page, IconSpinner } from '../../components';
import StatusPage from '../../components/Status/Status';
import {
  fetchReview,
  getCurrentReview,
  getCurrentReviewInProgress,
  getCurrentReviewError,
} from '../ReviewsPage/ReviewsPage.duck';
import { cleanListingTitle, decodeHTMLEntities } from '../../util/string';
import useRevealOnReady from '../../hooks/useRevealOnReady';
import revealCss from '../../styles/contentReveal.module.css';

import css from './ReviewDetailPage.module.css';

export { loadData } from '../ReviewsPage/ReviewsPage.duck';

const ReviewDetailPageComponent = props => {
  const {
    match,
    currentReview,
    currentReviewInProgress,
    currentReviewError,
    onFetchReview,
    schemaTitle,
    schemaDescription,
    ogImage,
    reviewState,
  } = props;
  const [readyReviewId, setReadyReviewId] = useState(null);

  const reviewId = match.params.id;
  const currentReviewId = currentReview?.id?.uuid || null;

  useEffect(() => {
    // Only fetch if we don't have the review data yet
    // (SSR will have already loaded it)
    if (reviewId && !currentReview && !currentReviewInProgress && !currentReviewError) {
      if (reviewState === 'draft' || reviewState === 'closed') {
        return;
      }
      onFetchReview(reviewId);
    }
  }, [reviewId, currentReview, currentReviewInProgress, currentReviewError, onFetchReview]);

  const isReviewMediaReady = Boolean(currentReviewId && readyReviewId === currentReviewId);
  const hasRevealedContent = useRevealOnReady(isReviewMediaReady, currentReviewId);
  const revealedContentClassName = classNames(revealCss.reveal, {
    [revealCss.hidden]: !hasRevealedContent,
    [revealCss.visible]: hasRevealedContent,
  });

  const handleReviewMediaReady = useCallback(() => {
    if (currentReviewId) {
      setReadyReviewId(currentReviewId);
    }
  }, [currentReviewId]);

  useEffect(() => {
    if (!currentReviewId) {
      setReadyReviewId(null);
      return;
    }

    setReadyReviewId(previousReadyReviewId =>
      previousReadyReviewId === currentReviewId ? previousReadyReviewId : null
    );
  }, [currentReviewId]);

  if (currentReviewInProgress) {
    return (
      <StatusPage schemaTitle={schemaTitle} schemaDescription={schemaDescription} ogImage={ogImage}>
        <div className={css.loading}>
          <IconSpinner />
        </div>
      </StatusPage>
    );
  }

  if (currentReviewError) {
    if (currentReviewError.status === 404) {
      return (
        <StatusPage
          schemaTitle="Review Not Found"
          schemaDescription="The requested review could not be found"
          ogImage={ogImage}
        >
          <div className={css.notFound}>Sorry, we can't find that review.</div>
          <ResalePlatformSection />
        </StatusPage>
      );
    }
    return (
      <StatusPage schemaTitle={schemaTitle} schemaDescription={schemaDescription} ogImage={ogImage}>
        <div className={css.error}>Error loading review: Please try again later.</div>
      </StatusPage>
    );
  }

  if (!currentReview) {
    return (
      <StatusPage
        schemaTitle="Review Not Found"
        schemaDescription="The requested review could not be found"
        ogImage={ogImage}
      >
        <div className={css.notFound}>Sorry, we can't find that review.</div>
        <ResalePlatformSection />
      </StatusPage>
    );
  }

  // Set up the page layout areas like other Sharetribe pages
  const layoutAreas = `
    topbar
    content
    platform
    main
    footer
  `;

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
      socialSharing={{ images1200: ogImage }}
    >
      {!isReviewMediaReady ? <div className={css.pageMask} aria-hidden="true" /> : null}
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Content, Platform, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="ReviewDetailPage" />
              </Topbar>
              <Content
                as="section"
                className={classNames(css.reviewContentSection, revealedContentClassName)}
              >
                <div className={css.reviewContentWrapper}>
                  <ReviewContent reviewData={currentReview} onReady={handleReviewMediaReady} />
                </div>
              </Content>
              <Platform
                as="section"
                className={classNames(css.platformSection, revealedContentClassName)}
              >
                <ResalePlatformSection />
              </Platform>
              <Main as="main" className={css.main}>
                <div className={css.content}>{/* Individual review content will go here */}</div>
              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => {
  const currentReview = getCurrentReview(state);
  const reviewState = currentReview?.attributes?.state;

  if (reviewState === 'draft' || reviewState === 'closed') {
    return {
      currentReview: null,
      currentReviewInProgress: getCurrentReviewInProgress(state),
      currentReviewError: getCurrentReviewError(state),
      schemaTitle: 'Review Not Available',
      schemaDescription: 'The requested review is not available',
      ogImage: [],
      reviewState,
    };
  }

  const title = currentReview?.attributes?.title || 'Review Detail';
  const brand = decodeHTMLEntities(currentReview?.attributes?.publicData.BrandName);
  const schemaTitle = `${brand} ${cleanListingTitle(title, brand)}`;
  const schemaDescription = currentReview?.attributes?.title
    ? `View detailed review for ${currentReview.attributes.title}`
    : 'View detailed review for marketplace listing';
  const ogImage =
    currentReview?.images.length > 0
      ? [currentReview.images[0].attributes.variants['scaled-large']]
      : [];

  return {
    currentReview,
    currentReviewInProgress: getCurrentReviewInProgress(state),
    currentReviewError: getCurrentReviewError(state),
    schemaTitle,
    schemaDescription,
    ogImage,
    reviewState,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchReview: reviewId => dispatch(fetchReview(reviewId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
const ReviewDetailPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ReviewDetailPageComponent);

export { ReviewDetailPageComponent };
export default ReviewDetailPage;
